import { BarChart3, GitCompareArrows, Activity, FileSpreadsheet, Scale, CalendarClock, Table, ClipboardCheck, Watch, LineChart, Pencil, Settings2, School, Users as UsersIcon, Ship, Layers, Stethoscope, SlidersHorizontal, Bug, Pill, RefreshCw, ArrowRightLeft, FilePlus2, Library, Package, Utensils, ClipboardList, PillBottle, TrendingUp, FlaskConical, Droplets } from "lucide-react";

// Central tab registry — imported by App.js for the top nav, and by
// HelpAssistant for the deep-link allow-list. Kept as a plain module
// so any consumer (unit test, storybook) can inspect the shape
// without instantiating React.
export const TABS = [
  { id: "overview",    label: "Squad Overview",   icon: BarChart3 },
  { id: "response",    label: "Supplement Response", icon: TrendingUp },
  { id: "advisory",    label: "Advisory",         icon: FlaskConical },
  { id: "sailor",      label: "Athlete Trend",    icon: Activity },
  { id: "compare",     label: "Compare Athletes", icon: GitCompareArrows },
  { id: "editor",      label: "Data Editor",      icon: FileSpreadsheet },
  { id: "spreadsheet", label: "Activity Log",     icon: Table },
  { id: "baselines",   label: "Baselines",        icon: ClipboardCheck },
  { id: "biometrics",  label: "Biometrics",       icon: Scale },
  { id: "wearables",   label: "Wearables",        icon: Watch },
  { id: "schedule",    label: "Schedule",         icon: CalendarClock },
  { id: "diet",        label: "Nutrition",        icon: Utensils },
  { id: "supplements", label: "Supplements",      icon: PillBottle },
  { id: "reports",     label: "Reports",          icon: ClipboardList },
];

// Tabs that get their own top-level pill in the header (alongside the
// grouped dropdowns), because the coach opens them daily and wants a
// one-click landing. Kept as a separate list so the ordering in the
// header can differ from the dropdown groups.
// (2026-02-23) Nutrition + Supplements moved into the Data group to
// tighten the header row; the Data dropdown now covers every
// "edit an athlete's records" screen.
export const TOP_LEVEL_TABS = [];

// Grouped nav — collapses the 9 flat tabs into 3 dropdown groups so
// the top bar stops sprawling as we add more screens. Each group's
// `tabs` array preserves display order inside its dropdown. Sub-items
// still reference the SAME tab IDs as `TABS` above so the router,
// deep-link allow-list and `data-testid="tab-<id>"` selectors keep
// working unchanged.
export const NAV_GROUPS = [
  {
    id: "insights",
    label: "Insights",
    icon: LineChart,
    tabIds: ["overview", "response", "advisory", "compare"],
  },
  {
    id: "data",
    label: "Data",
    icon: Pencil,
    // Athlete Trend (id="sailor") is folded INSIDE the Data Editor via
    // a "Trend | Edit" sub-pill (2026-02-20 per user request "move the
    // athlete trend into Data Editor"). We keep the `sailor` tab id
    // alive for URL / testid / localStorage backward-compat but no
    // longer surface it as its own dropdown entry. `aliasTabIds` keeps
    // the Data pill highlighted while the sub-toggle is on Trend.
    tabIds: ["editor", "spreadsheet", "baselines", "biometrics", "diet", "supplements"],
    aliasTabIds: ["sailor", "marker_trends"],
  },
  {
    id: "ops",
    label: "Ops",
    icon: Settings2,
    tabIds: ["wearables", "schedule", "reports"],
  },
];

// Modal-based nav groups — sit in the top nav alongside the tab groups
// above. Each entry opens a modal (not a tab) so we route them through
// the `ych:open` custom event bus that `UserMenu` already listens on
// (added originally for HelpAssistant deep-links). This keeps the app's
// existing tab-router untouched while consolidating the "Schools
// master · Squads master …" fragmented Avatar-dropdown into a
// first-class navigation surface (2026-02-15 UX consolidation).
//
// Each item's `route` matches a key in UserMenu's event map. Items
// with a `tab` field switch tabs instead of dispatching a modal event
// — used for bulk-actions that already live inside a tab's toolbar
// (e.g. lab PDF imports live in the Data Editor toolbar).
export const MODAL_NAV_GROUPS = [
  {
    id: "masters",
    label: "Masters",
    icon: Library,
    items: [
      { id: "schools",   route: "menu:schools",   label: "Schools",           icon: School,             tone: "cyan",    tooltip: "Controlled vocabulary for the School / Academy dropdown on every baseline" },
      { id: "squads",    route: "menu:squads",    label: "Squads",            icon: UsersIcon,          tone: "cyan",    tooltip: "Add / rename / retire squads (MJPT, AGAPE, Current Athletes…). Renames cascade onto every athlete." },
      { id: "classes",   route: "menu:classes",   label: "Classes",           icon: Ship,               tone: "amber",   tooltip: "Boat / dinghy classes (Opti A, 29er, ILCA 6, …). Shown on each athlete's row." },
      { id: "levels",    route: "menu:levels",    label: "Levels",            icon: Layers,             tone: "emerald", tooltip: "Performance tier (Beginner → Elite). Used on reports and the Response Tracker." },
      { id: "blood",     route: "menu:blood_groups", label: "Blood groups",   icon: Droplets,           tone: "rose",    tooltip: "Add rare phenotypes (Bombay Oh, Rh-null). Standard 8 ABO/Rh groups are pre-seeded." },
      { id: "doctors",   route: "menu:doctors",   label: "Doctors",           icon: Stethoscope,        tone: "cyan",    tooltip: "Panel of doctors / specialists for consultation records. Includes contact details." },
      { id: "ranges",    route: "menu:ranges",    label: "Reference ranges",  icon: SlidersHorizontal,  tone: "cyan",   adminOnly: true, tooltip: "Admin only — override lab reference ranges per marker, per squad, per gender" },
    ],
  },
  {
    id: "bulk",
    label: "Bulk",
    icon: Package,
    items: [
      // Lab PDF wizard is a singleton mounted in UserMenu (same as the
      // deworming / supplement / squad bulk modals) so it can be
      // opened directly from anywhere in the app — not just from the
      // Data Editor toolbar.
      { id: "bulk-lab",         route: "menu:bulk_labs",           label: "New: Lab PDFs",                icon: FilePlus2,        tone: "cyan",    tooltip: "Upload one or many lab PDFs — AI scan extracts markers into draft records for review" },
      { id: "bulk-deworming",   route: "menu:bulk_deworming",      label: "New: Deworming batch",         icon: Bug,              tone: "emerald", tooltip: "Log a deworming dose for an entire squad in one click" },
      { id: "bulk-supplement",  route: "menu:bulk_supplement",     label: "New: Supplement batch",        icon: Pill,             tone: "violet",  tooltip: "Prescribe the same supplement to multiple athletes at once" },
      { id: "bulk-squad",       route: "menu:bulk_squad",          label: "Reassign squad / class",       icon: ArrowRightLeft,   tone: "amber",   tooltip: "Spreadsheet view — bulk move athletes between squads and classes" },
      { id: "bulk-batches",     route: "menu:bulk_batches",        label: "Edit recent batches",          icon: RefreshCw,        tone: "cyan",    tooltip: "Fix typos across an entire batch (e.g. wrong date) — undoes and re-saves atomically" },
    ],
  },
];

// Page-title strip content shown on every tab except Squad Overview.
// The subtitle mirrors what the coach is looking at right now — brief,
// consistent with the tab's own in-panel stats/context bar.
export const TAB_TITLES = {
  response:   "Supplement Response Tracker",
  advisory:   "Advisory",
  sailor:     "Athlete trend",
  marker_trends: "Marker trends",
  compare:    "Compare athletes",
  editor:     "Data editor",
  spreadsheet:"Activity log",
  baselines:  "Baselines",
  biometrics: "Biometrics",
  wearables:  "Wearables",
  schedule:   "Schedule",
  diet:       "Nutrition",
  supplements:"Supplements",
  reports:    "Reports",
};

export const TAB_SUBTITLES = {
  response:   "Which supplement regimens are working · who to review",
  advisory:   "Who to test, when, and for what · plus supplement neglect alerts",
  sailor:     "Longitudinal timeline · one athlete",
  marker_trends: "Selected markers over time · with supplement events",
  compare:    "Side-by-side markers · multiple athletes",
  editor:     "CRUD · labs, consultations, supplements, injuries",
  spreadsheet:"One unified feed · adds, edits, deletes · filter by action / entity",
  baselines:  "One row per athlete · double-click to edit full baseline",
  biometrics: "Height · weight · BMI · growth insights",
  wearables:  "WHOOP recovery · strain · sleep matrix",
  schedule:   "3-month diagnostics · 6-month deworming",
  diet:       "Editable weekly menu · squad marker-gap analysis",
  supplements:"Monthly adherence grid · prescriptions · overdose warnings",
};

// IDs allowed as direct top-level tab jumps in the HelpAssistant deep-link
// router. Kept in sync with TABS so a stray dep-typo produces a runtime
// no-op instead of a silent redirect to a non-existent screen.
export const TAB_IDS = TABS.map((t) => t.id);
